import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PersianDatePicker from '../components/PersianDatePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';
import moment from 'moment-jalaali';

interface Appointment {
  id: number;
  user_id: number;
  service_id?: number;
  status: string;
  step_completed: number;
  appointment_date?: string;
  appointment_time?: string;
  tracking_code?: string;
  created_at: string;
  user?: {
    full_name: string;
    phone: string;
  };
  service?: {
    name: string;
  };
}

const AdminTodayAppointments: React.FC = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(moment().format('jYYYY/jMM/jDD'));
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const url = `${apiBaseUrl}/admin/appointments/today-completed?date=${encodeURIComponent(selectedDate)}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('خطا در دریافت نوبت‌ها');
      }

      const data = await response.json();
      setAppointments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطای ناشناخته');
    } finally {
      setLoading(false);
    }
  };

  // گروه‌بندی نوبت‌ها بر اساس ساعت
  const groupAppointmentsByHour = () => {
    const grouped: { [hour: string]: Appointment[] } = {};

    appointments.forEach((appointment) => {
      if (appointment.appointment_time) {
        const hour = appointment.appointment_time.split(':')[0]; // فقط ساعت را بگیر
        if (!grouped[hour]) {
          grouped[hour] = [];
        }
        grouped[hour].push(appointment);
      }
    });

    // مرتب‌سازی ساعت‌ها
    const sortedHours = Object.keys(grouped).sort((a, b) => {
      const hourA = parseInt(a);
      const hourB = parseInt(b);
      return hourA - hourB;
    });

    return { grouped, sortedHours };
  };

  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedAppointment(null);
  };

  const handleViewFullDetails = (id: number) => {
    closeDetailModal();
    navigate(`/admin/appointments/${id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">نوبت‌های نهایی شده</h1>

          <div className="flex items-center gap-4 mb-4">
            <label className="text-sm font-medium text-gray-700">انتخاب تاریخ:</label>
            <div className="w-64">
              <PersianDatePicker
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                placeholder="تاریخ را انتخاب کنید"
              />
            </div>
            <button
              onClick={() => setSelectedDate(moment().format('jYYYY/jMM/jDD'))}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              امروز
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {appointments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 text-lg">نوبت نهایی شده‌ای برای این تاریخ یافت نشد.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {(() => {
              const { grouped, sortedHours } = groupAppointmentsByHour();
              return sortedHours.map((hour) => (
                <div
                  key={hour}
                  className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
                >
                  <div className="bg-blue-600 text-white px-4 py-3">
                    <h3 className="text-lg font-bold text-center">
                      {hour.padStart(2, '0')}:00
                    </h3>
                  </div>
                  <div className="p-4">
                    {grouped[hour].length === 0 ? (
                      <p className="text-gray-500 text-sm text-center">نوبتی وجود ندارد</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {grouped[hour].map((appointment) => (
                          <button
                            key={appointment.id}
                            onClick={() => handleViewDetails(appointment)}
                            className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium cursor-pointer"
                          >
                            #{appointment.tracking_code || (1000 + appointment.id)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}

        {/* مودال جزئیات نوبت */}
        {showDetailModal && selectedAppointment && (
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto"
            onClick={closeDetailModal}
            style={{ direction: 'ltr' }}
          >
            <div className="min-h-full flex items-center justify-center p-4">
              <div
                className="relative p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto mx-auto"
                onClick={(e) => e.stopPropagation()}
                style={{ direction: 'rtl' }}
              >
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    جزئیات نوبت #{selectedAppointment.tracking_code || (1000 + selectedAppointment.id)}
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          نام و نام خانوادگی
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                          {selectedAppointment.user?.full_name || '-'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          شماره تماس
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                          {selectedAppointment.user?.phone || '-'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          خدمت
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                          {selectedAppointment.service?.name || 'نامشخص'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          تاریخ مراجعه
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                          {selectedAppointment.appointment_date || '-'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ساعت مراجعه
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                          {selectedAppointment.appointment_time || '-'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          تاریخ ایجاد
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                          {selectedAppointment.created_at ? new Date(selectedAppointment.created_at).toLocaleString('fa-IR', {
                            timeZone: 'Asia/Tehran',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }) : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={closeDetailModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    بستن
                  </button>
                  <button
                    onClick={() => handleViewFullDetails(selectedAppointment.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    مشاهده جزئیات کامل
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTodayAppointments;

